import { useNavigate, useLocation } from 'react-router-dom';
import { useSystemSettings } from '../hooks/useSystemSettings';

export default function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSystemSettings();
  const logoUrl = settings.logo_small_url || settings.logo_url;
  const platformName = settings.platform_name;

  // Smart back: if there's real app history, go back; otherwise fall to a safe default
  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else if (location.pathname.startsWith('/admin')) {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  };

  // Smart home: admin context goes to /admin, otherwise /dashboard
  const handleHome = () => {
    if (location.pathname.startsWith('/admin')) {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6 bg-white relative overflow-hidden"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 opacity-60" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gray-50 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 opacity-60" />
        <p className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[180px] font-900 text-gray-50 select-none tracking-tighter leading-none">
          404
        </p>
      </div>

      <div className="relative z-10 w-full max-w-md text-center">
        {(logoUrl || platformName) && (
          <div className="flex items-center justify-center gap-2 mb-10">
            {logoUrl && <img src={logoUrl} alt={platformName} className="w-8 h-8 rounded-lg object-cover" />}
            {platformName && <span className="text-base font-700 text-gray-700">{platformName}</span>}
          </div>
        )}

        <div className="w-16 h-16 flex items-center justify-center bg-gray-50 border border-gray-100 rounded-2xl mx-auto mb-6">
          <i className="ri-compass-discover-line text-3xl text-gray-400" />
        </div>

        <h1 className="text-2xl font-700 text-gray-900 mb-3">Página não encontrada</h1>
        <p className="text-gray-500 text-sm leading-relaxed mb-2 max-w-sm mx-auto">
          A página que você está procurando não existe ou foi movida para outro endereço.
        </p>

        <div className="mb-8 px-3 py-2 bg-gray-50 rounded-xl inline-block">
          <span className="text-xs text-gray-400 font-mono">{location.pathname}</span>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-arrow-left-line text-sm" />
            Voltar
          </button>
          <button
            onClick={handleHome}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-home-line text-sm" />
            Ir para o início
          </button>
        </div>

        <div className="mt-8">
          <p className="text-xs text-gray-400 mb-3">Links rápidos</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {[
              { label: 'Dashboard', path: '/dashboard', icon: 'ri-dashboard-line' },
              { label: 'Lançamentos', path: '/lancamentos', icon: 'ri-exchange-dollar-line' },
              { label: 'Metas', path: '/metas', icon: 'ri-focus-3-line' },
              { label: 'Perfil', path: '/perfil', icon: 'ri-user-line' },
            ].map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-emerald-50 border border-gray-100 hover:border-emerald-200 text-gray-600 hover:text-emerald-700 text-xs font-500 rounded-xl cursor-pointer transition-all"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={`${link.icon} text-xs`} />
                </div>
                {link.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
